import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Check, 
  X, 
  Loader2,
  Clock,
  Trash2
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface FamilyMember {
  id: string;
  member_email: string;
  status: 'pending' | 'accepted' | 'rejected';
  invited_at: string;
  accepted_at: string | null;
}

interface FamilyMemberManagerProps {
  userId: string;
  plan: string;
  isVisible: boolean;
}

const MAX_FAMILY_MEMBERS = 2; // Familie plan allows 2 additional members

export function FamilyMemberManager({ userId, plan, isVisible }: FamilyMemberManagerProps) {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();

  const canAddMore = members.length < MAX_FAMILY_MEMBERS;
  const isFamilyPlan = plan === 'familie';

  const fetchMembers = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('klartext_family_members')
        .select('*')
        .eq('owner_id', userId)
        .order('invited_at', { ascending: false });

      if (error) throw error;
      setMembers((data || []) as any);
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && userId && isFamilyPlan) {
      fetchMembers();
    }
  }, [isVisible, userId, isFamilyPlan]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !canAddMore) return;

    setInviting(true);
    try {
      // Call the edge function to handle invitation + email sending
      const { data, error } = await supabase.functions.invoke('klartext-family-invite', {
        body: { member_email: inviteEmail.trim().toLowerCase() }
      });

      if (error) {
        throw new Error(error.message || 'Einladung konnte nicht gesendet werden.');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.member) {
        setMembers(prev => [data.member, ...prev]);
      }
      
      setInviteEmail('');
      toast({
        title: 'Einladung gesendet',
        description: data.email_sent 
          ? `Eine Einladungs-E-Mail wurde an ${inviteEmail} gesendet.`
          : `Einladung erstellt für ${inviteEmail}.`,
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Einladung konnte nicht gesendet werden.',
        variant: 'destructive',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('klartext_family_members')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setMembers(prev => prev.filter(m => m.id !== id));
      toast({
        title: 'Mitglied entfernt',
        description: 'Das Familienmitglied wurde entfernt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: 'Mitglied konnte nicht entfernt werden.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (!isVisible || !isFamilyPlan) return null;

  const statusConfig = {
    pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Ausstehend' },
    accepted: { icon: Check, color: 'text-green-600', bg: 'bg-green-50', label: 'Akzeptiert' },
    rejected: { icon: X, color: 'text-red-600', bg: 'bg-red-50', label: 'Abgelehnt' }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" />
          Familienmitglieder
        </CardTitle>
        <CardDescription>
          Laden Sie bis zu {MAX_FAMILY_MEMBERS} Familienmitglieder ein, um unbegrenzte Checks zu teilen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Invite Form */}
        <form onSubmit={handleInvite} className="flex gap-2 mb-4">
          <Input
            type="email"
            placeholder="E-Mail-Adresse eingeben..."
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={!canAddMore || inviting}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={!canAddMore || inviting || !inviteEmail.trim()}
            className="gap-2"
          >
            {inviting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Einladen
          </Button>
        </form>

        {!canAddMore && (
          <p className="text-sm text-amber-600 mb-4">
            Maximum von {MAX_FAMILY_MEMBERS} Familienmitgliedern erreicht.
          </p>
        )}

        {/* Members List */}
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Noch keine Familienmitglieder eingeladen.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const status = statusConfig[member.status];
              const StatusIcon = status.icon;

              return (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${status.bg}`}
                >
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-900">{member.member_email}</p>
                      <div className="flex items-center gap-1 text-xs">
                        <StatusIcon className={`h-3 w-3 ${status.color}`} />
                        <span className={status.color}>{status.label}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(member.id)}
                    disabled={deletingId === member.id}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                  >
                    {deletingId === member.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
